import { useState } from "react";
import { FaChevronLeft } from "react-icons/fa";
import { useNavigate, useOutletContext } from "react-router-dom";
import MeritoButton from "../../MeritoButtons/MeritoButtons";
import type { TallyConnectionFlowResult } from "../../../Hooks/useTallyConnectionFlow";
import { TallyConstantKey, TallyConstants } from "../constants";
import TallyResponsiveFrame from "../TallyResponsiveFrame";
import sharedStyles from "../TallySyncShared.module.scss";
import styles from "./TallySync.module.scss";

const supplierIntelligenceBasePath = TallyConstants.get(
  TallyConstantKey.SupplierIntelligenceBasePath
);
const tallyMainPageImageUrl = TallyConstants.get(
  TallyConstantKey.TallyMainPageImageUrl
);

interface TallySyncProps {
  isDashboard?: boolean;
  onDashboardDownload?: () => Promise<void>;
}

export default function TallySync({
  isDashboard = false,
  onDashboardDownload,
}: TallySyncProps) {
  const navigate = useNavigate();
  const outletContext = useOutletContext<TallyConnectionFlowResult | undefined>();
  const [downloading, setDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState<string | null>(null);

  const downloadTdl = isDashboard
    ? onDashboardDownload
    : outletContext?.downloadTdl;

  const handleDownload = async (continueToInstall: boolean) => {
    if (!downloadTdl) return;

    setDownloading(true);
    setDownloadError(null);
    try {
      await downloadTdl();
      if (continueToInstall) navigate("install");
    } catch (error: any) {
      if (isDashboard) {
        setDownloadError(
          error?.response?.data?.message ||
            error?.message ||
            "Could not download the TDL file. Please try again."
        );
      }
    } finally {
      setDownloading(false);
    }
  };

  const stageContent = (
    <div
      className={
        isDashboard ? styles.dashboardContent : sharedStyles.content
      }
    >
      {!isDashboard && (
        <div className={sharedStyles.header}>
          <p className={sharedStyles.stepLabel}>
            STEP 2 OF 2 • Connect Automatically
          </p>
          <p className={sharedStyles.heading}>Set Up Automatic Sync</p>
        </div>
      )}

      <div
        className={`${styles.instructions} ${
          isDashboard ? styles.dashboardInstructions : ""
        }`}
      >
        <div className={styles.screenshotPanel}>
          <div className={styles.screenshotFrame}>
            <img
              src={tallyMainPageImageUrl}
              alt="Tally Gateway screen"
              className={styles.screenshotImage}
            />
          </div>
        </div>

        <div className={styles.stepsPanel}>
          {isDashboard && (
            <div className={styles.dashboardHeader}>
              <h2>Set Up Automatic Sync</h2>
              <p>Sync directly from Tally. No manual exports</p>
            </div>
          )}

          <div className={styles.stepRow}>
            <div className={styles.stepCopy}>
              <span className={sharedStyles.stepBadge}>1</span>
              <p className={styles.stepText}>Download the TDL file.</p>
            </div>
            {isDashboard && (
              <MeritoButton
                type="outline-dim"
                size="sm"
                className={styles.downloadBtn}
                extraStyles={{
                  padding: "0.34375rem",
                  borderRadius: "0.25rem",
                }}
                disabled={downloading}
                onClick={() => handleDownload(false)}
                btnText={downloading ? "Downloading…" : "Download File"}
              />
            )}
          </div>

          {downloadError && (
            <p className={styles.downloadError} role="alert">
              {downloadError}
            </p>
          )}
        </div>
      </div>
    </div>
  );

  if (isDashboard) return stageContent;

  return (
    <TallyResponsiveFrame>
      <div className={sharedStyles.page}>
        <div className={sharedStyles.shell}>
          <MeritoButton
            type="secondary-1"
            size="md2"
            className={sharedStyles.backBtn}
            onClick={() =>
              navigate(
                `${supplierIntelligenceBasePath}/setup/upload/tally/connect`,
                { replace: true }
              )
            }
            icon={
              <span className={sharedStyles.backIcon} aria-hidden="true">
                <FaChevronLeft />
              </span>
            }
            btnText="Back"
          />

          <div className={sharedStyles.card}>
            {stageContent}

            <div className={styles.footer}>
              <MeritoButton
                type="primary-1"
                size="md2"
                className={styles.continueBtn}
                disabled={downloading}
                onClick={() => handleDownload(true)}
                btnText={
                  downloading ? "Downloading…" : "Download and Continue"
                }
              />
            </div>
          </div>
        </div>
      </div>
    </TallyResponsiveFrame>
  );
}
